import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { noticeToCreditorsFlow } from "../index";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { OUTCOMES } from "../../../constants";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CONSTANTS } from "@theatticusapp/atticus-shared-components";

interface Values {
  executorEmail: string;
  executorPhone: string;
  executorAddress: string;
}

interface Props {
  executorEmail?: string;
  executorPhone?: string;
  executorAddress?: string;
}

export const BizContactInfo = ({ executorEmail }: Props): JSX.Element => {
  const navigate = useNavigate();
  const { screenName } = useParams();
  const currentScreen = noticeToCreditorsFlow.getCurrentFlowScreen(screenName);

  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      executorEmail: "",
      executorPhone: "",
      executorAddress: "",
    },
    schema: Yup.object().shape({
      executorEmail: Yup.string().email(),
      executorPhone: Yup.string().matches(
        CONSTANTS.REGEX.phone,
        "Phone number is not valid"
      ),
      executorAddress: Yup.string().required(),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      if (!currentScreen) throw Error("couldn't determine currentScreen");

      // TODO: save to database

      const nextPath = noticeToCreditorsFlow.getNextScreen(
        currentScreen,
        OUTCOMES.complete
      );
      navigate(nextPath);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      // console.error(error);
      setSubmitting(false);
    }
  };

  return currentScreen ? (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: 800,
          py: 2,
        }}
      >
        <Typography component="h2" variant="h5">
          {executorEmail || "What is [Gerald executor’s] contact info?"}
        </Typography>
        <Formik
          initialValues={formikInit.values}
          validationSchema={formikInit.schema}
          onSubmit={handleSubmit}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
            status,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid
                container
                spacing={1}
                sx={{
                  my: 1,
                }}
              >
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.executorEmail && errors.executorEmail
                    )}
                    fullWidth
                    helperText={touched.executorEmail && errors.executorEmail}
                    label="Email"
                    margin="normal"
                    name="executorEmail"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.executorEmail}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.executorPhone && errors.executorPhone
                    )}
                    fullWidth
                    helperText={touched.executorPhone && errors.executorPhone}
                    label="Phone number"
                    margin="normal"
                    name="executorPhone"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.executorPhone}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    error={Boolean(
                      touched.executorAddress && errors.executorAddress
                    )}
                    fullWidth
                    helperText={
                      touched.executorAddress && errors.executorAddress
                    }
                    label="Address"
                    margin="normal"
                    name="executorAddress"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.executorAddress}
                    variant="outlined"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{
                    my: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    color="error"
                    style={{ marginRight: 8 }}
                  >
                    {status}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <IconButton
                      aria-label="back"
                      onClick={() => navigate(-1)}
                      sx={{ mr: 2 }}
                      className="inverse"
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Continue
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Box>
    </>
  ) : (
    <></>
  );
};
